import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Eye, ServerCog } from "lucide-react";
import { useTableSort, SortTh } from "@/lib/tableSort";
import { InfraFilters } from "@/pages/NetworkOdc";
import { ServerFormDialog } from "@/components/network/ServerFormDialog";
import { ServerDetail } from "@/components/network/ServerDetail";
import { StatusPill, NoCoordBadge } from "@/components/network/StatusPill";
import { OldKodeLabel } from "@/components/network/OldKodeLabel";
import { ArchiveInfraButton } from "@/components/network/ArchiveInfraButton";
import { InfraCards, InfraCard } from "@/components/network/InfraCards";
import { useNetworkRefs } from "@/lib/network";

const ARSIP = (k) => <span className="ml-1 rounded bg-[#E5E7EB] px-1 text-[10px] font-semibold text-[#374151]" data-testid={`server-archived-badge-${k}`}>ARSIP</span>;

export default function NetworkServer() {
  const { refs, wilayahName, lokasiName } = useNetworkRefs();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ q: "", lokasi_id: "", wilayah_id: "", status: "", archived: false });
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(sp.get("server_id"));
  const load = useCallback(() => api.get("/network/servers", { params: f }).then((r) => setRows(r.data)), [f]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const getters = useMemo(() => ({ kode: (o) => o.kode, nama: (o) => o.nama, wilayah: (o) => wilayahName(o.wilayah_id), lokasi: (o) => lokasiName(o.lokasi_id),
    anggota: (o) => o.member_count, status: (o) => o.status }), [wilayahName, lokasiName]);
  const st = useTableSort("server", rows, getters);
  const me = refs?.me;
  const canManage = !!me?.can_manage;
  const cols = [["kode", "Kode"], ["nama", "Nama"], ["wilayah", "Wilayah"], ["lokasi", "Lokasi"], ["anggota", "Anggota"], ["status", "Status"]];
  const emptyText = f.archived ? "Tidak ada SERVER yang diarsipkan." : `Belum ada SERVER${Object.values(f).some(Boolean) ? " untuk filter ini" : ""}.`;
  const coord = (o) => (o.has_coords ? <span className="font-mono-tab text-[11px]">{o.latitude}, {o.longitude}</span> : <NoCoordBadge testid={`server-nocoord-${o.kode}`} />);
  return (
    <div className="space-y-4 p-4 sm:p-6" data-testid="network-server-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="font-display text-2xl font-bold">Master SERVER</h1>
          <p className="text-sm text-[#6B7280]">Kontainer virtual pengelompokan OLT, OTB, dan Splitter{canManage ? "" : " (mode lihat)"}</p></div>
        {canManage && <Button onClick={() => setForm({})} data-testid="server-add-btn"><Plus className="mr-1 h-4 w-4" />Tambah SERVER</Button>}
      </div>
      <InfraFilters refs={refs} f={f} setF={setF} prefix="server" placeholder="Cari kode, kode lama, nama, alamat…">
        <label className="flex items-center gap-1.5 text-xs" data-testid="server-filter-archived">
          <input type="checkbox" checked={f.archived} onChange={(e) => setF((x) => ({ ...x, archived: e.target.checked }))} data-testid="server-filter-archived-toggle" />Tampilkan Arsip</label>
      </InfraFilters>
      <InfraCards prefix="server" st={st} cols={cols} empty={rows && !rows.length ? emptyText : null} render={(o) => (
        <InfraCard key={o.id} prefix="server" row={o} q={f.q}
                   sub={<><span className={o.wilayah_valid === false ? "text-[#B45309]" : ""}>{wilayahName(o.wilayah_id)}</span> · {lokasiName(o.lokasi_id)}</>}
                   info={<span data-testid={`server-card-count-${o.kode}`}>{o.member_count} anggota</span>}
                   actions={<>
                     <Button size="icon" variant="ghost" onClick={() => setDetail(o.id)} data-testid={`server-card-view-${o.kode}`}><Eye className="h-4 w-4" /></Button>
                     <ArchiveInfraButton iconOnly kind="server" item={o} me={me} onDone={load} tid={`server-card-${o.archived ? "restore" : "archive"}-${o.kode}`} />
                     {canManage && !o.archived && <Button size="icon" variant="ghost" onClick={() => setForm(o)} data-testid={`server-card-edit-${o.kode}`}><Pencil className="h-4 w-4" /></Button>}
                   </>} />)} />
      <div className="hidden overflow-x-auto rounded-lg border border-[#E5E7EB] bg-white md:block">
        <table className="w-full min-w-[760px] text-sm" data-testid="server-table">
          <thead className="bg-[#F8F9FA] text-left text-xs uppercase text-[#6B7280]">
            <tr>{cols.map(([c, l], i) => <SortTh key={c} className={i ? "" : "p-2"} col={c} label={l} s={st} testid="server" />)}<th>Koordinat</th><th className="pr-2 text-right">Aksi</th></tr>
          </thead>
          <tbody>
            {(st.sorted || []).map((o) => (
              <tr key={o.id} className="border-t" data-testid={`server-row-${o.kode}`}>
                <td className="p-2 font-mono-tab font-semibold">{o.kode}<OldKodeLabel row={o} q={f.q} testid={`server-oldkode-${o.kode}`} />{o.archived && ARSIP(o.kode)}</td>
                <td>{o.nama}</td><td className={o.wilayah_valid === false ? "text-[#B45309]" : ""}>{wilayahName(o.wilayah_id)}</td><td>{lokasiName(o.lokasi_id)}</td>
                <td data-testid={`server-count-${o.kode}`}>{o.member_count}</td><td><StatusPill status={o.status} /></td><td>{coord(o)}</td>
                <td className="whitespace-nowrap pr-2 text-right">
                  <Button size="icon" variant="ghost" onClick={() => setDetail(o.id)} data-testid={`server-view-${o.kode}`}><Eye className="h-4 w-4" /></Button>
                  <ArchiveInfraButton iconOnly kind="server" item={o} me={me} onDone={load} />
                  {canManage && !o.archived && <Button size="icon" variant="ghost" onClick={() => setForm(o)} data-testid={`server-edit-${o.kode}`}><Pencil className="h-4 w-4" /></Button>}
                </td>
              </tr>
            ))}
            {rows && !rows.length && <tr><td colSpan={8} className="p-8 text-center text-[#6B7280]" data-testid="server-empty">
              <ServerCog className="mx-auto mb-2 h-8 w-8 opacity-40" />{emptyText}</td></tr>}
          </tbody>
        </table>
      </div>
      <ServerFormDialog open={!!form} editing={form?.id ? form : null} refs={refs} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-h-[92dvh] max-w-2xl overflow-y-auto" data-testid="server-detail-dialog">
          <DialogHeader><DialogTitle>Detail SERVER</DialogTitle></DialogHeader>
          {detail && refs && <ServerDetail serverId={detail} me={me} wilayahName={wilayahName} lokasiName={lokasiName} onChanged={load}
                                           onArchived={() => { setDetail(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
